/*
Copyright (C) 2009 - 2019 Broadleaf Commerce.

Licensed under the Broadleaf End User License Agreement (EULA),
Version 1.1 (the “Commercial License” located at
http://license.broadleafcommerce.org/commercial_license-1.1.txt).

Alternatively, the Commercial License may be replaced with a mutually
agreed upon license (the “Custom License”) between you and
Broadleaf Commerce. You may not use this file except in compliance
with the applicable license.
*/
import React, { useState } from 'react';
import { hooks, unstable, utils } from '@broadleaf/admin-components';
import cx from 'classnames';
import { get } from 'lodash';
import { useParams } from 'react-router-dom';

import {
  failureFetch,
  queueFetch
} from 'app/oms/components/AuroraOrderView/state/actions/fetchActions';

const { useTracking } = hooks;
const { ContextRequest, Spinner, MetadataUtils } = unstable;
const { RequestUtils } = utils;

const { ContextParameters } = ContextRequest;
const { request } = RequestUtils;
const { getEndpointByType } = MetadataUtils;

const Acknowledge = ({
  actionDefinition,
  className,
  dispatch,
  handleClose,
  metadata,
  state
}) => {
  const isDisabled = get(state, 'data.status') !== 'SUBMITTED';
  const config = getEndpointByType(actionDefinition, 'ACKNOWLEDGE');
  const contextParams = useContextParams(metadata, state);
  const [loading, setLoading] = useState(false);
  const handle = React.useCallback(async () => {
    try {
      setLoading(true);
      await request(config, contextParams);
      setLoading(false);
      dispatch(queueFetch());
    } catch (err) {
      setLoading(false);
      if (!err.cancelled) {
        dispatch(failureFetch(err));
      }
    } finally {
      handleClose();
    }
  }, [config, contextParams, dispatch, handleClose]);
  return (
    <button
      className={cx(
        'tw-relative tw-w-full tw-px-3 tw-py-4',
        'tw-border-t tw-border-gray-300 tw-bg-white tw-text-center tw-text-lg tw-font-medium tw-text-green-700',
        'tw-whitespace-no-wrap tw-truncate',
        'hover:tw-bg-green-100 hover:tw-text-green-700 focus:tw-bg-green-100 focus:tw-text-green-700 focus:tw-outline-none active:tw-bg-green-200 disabled:tw-cursor-not-allowed disabled:tw-bg-gray-200 disabled:tw-text-gray-700',
        'md:tw-py-2 md:tw-text-sm',
        { [className]: !!className, 'tw-cursor-no-allowed': loading }
      )}
      disabled={isDisabled}
      onClick={() => {
        if (isDisabled || loading) {
          return;
        }
        handle();
      }}
    >
      <span className={cx({ 'tw-text-transparent': loading })}>
        {actionDefinition.label}
      </span>
      {loading && (
        <div className="tw-absolute tw-inset-0 tw-flex tw-items-center tw-justify-center">
          <Spinner color="info" />
        </div>
      )}
    </button>
  );
};

function useContextParams(metadata, state) {
  const { id } = useParams();
  const tracking = useTracking(metadata);

  return React.useMemo(() => {
    let contextParams = {};
    if (id) {
      contextParams.id = id;
    }

    if (tracking) {
      contextParams[ContextParameters.TRACKING] = tracking;
    }

    if (state && state.data) {
      contextParams.parent = state.data;

      if (state.data.contextState) {
        contextParams[ContextParameters.CONTEXT_STATE] =
          state.data.contextState;
      }
    }

    return contextParams;
  }, [id, state, tracking]);
}

export default Acknowledge;
export { Acknowledge };
